import getProviderExperience from '@utils/profile/get-provider-experience';
import { isNil } from 'lodash/lang';
import { getStateInfo } from '@utils/get-state';
import { getPostDetailsData } from '@utils/enquiry/get-enquiry-details';
import getOfferDetailsData from '@utils/offer/get-offer-details-data';

const ENQUIRY_SOURCE = 'Marketplace';

export function enquiryUuidPayload(user, enquiry) {
  if (!user || !user.uuid) {
    return { enquiry_id: enquiry.uuid };
  }

  return {
    user_id: user.uuid,
    enquiry_id: enquiry.uuid,
  };
}

export function enquiryPayload(user, enquiry) {
  const homeLoan = enquiry.target_resources[0];

  const postDetailsData = getPostDetailsData(enquiry);
  const fetchedPurpose = postDetailsData.find((key) => key.title === 'Loan Purpose').value;
  const fetchedType = postDetailsData.find((key) => key.title === 'Loan Type').value;

  return {
    user_id: user.uuid,
    enquiry_id: enquiry.uuid,
    enquiry_stage: getStateInfo(enquiry).state.value,
    loan_type: fetchedType,
    property_type: fetchedPurpose,
    property_market_value: homeLoan.data.property_market_value,
    loan_amount: homeLoan.data.value ? homeLoan.data.value : null,
    offers_received: enquiry.offers.length || 0,
  };
}

export function offerPayload(user, enquiry, offer) {
  const postDetailsData = getPostDetailsData(enquiry);
  const offerDetailsData = getOfferDetailsData(offer, enquiry);

  const homeLoan = enquiry.target_resources[0];

  const applicationFeesValue = offer.application_fee_value;

  const fetchedPurpose = postDetailsData.find((key) => key.title === 'Loan Purpose').value;
  const fetchedType = postDetailsData.find((key) => key.title === 'Loan Type').value;

  const fetchedLenderCategory = offerDetailsData.organisation.find((key) => key.label === 'Lender category');
  const fetchedRepaymentType = offerDetailsData.features.find((key) => key.label === 'Repayment Type');

  return {
    user_id: user.uuid,
    enquiry_id: enquiry.uuid,
    enquiry_stage: getStateInfo(enquiry).state.value,
    enquiry_source: ENQUIRY_SOURCE,
    loan_type: fetchedType,
    property_type: fetchedPurpose,
    property_market_value: homeLoan.data.property_market_value,
    loan_amount: homeLoan.data.value ? homeLoan.data.value : null,
    offer_id: offer.uuid,
    organisation_id: offer.fsp.organisation.uuid,
    organisation_name: offer.fsp.organisation.name,
    provider_type: toUpper(offer.fsp.provider_type),
    lender_name: offer.organisation.name,
    lender_category: fetchedLenderCategory && fetchedLenderCategory.value ?
      fetchedLenderCategory.value : undefined,
    rate_type: toUpper(offer.rate_type),
    fixed_period: offer.fixed_period ? parseInt(offer.fixed_period, 10) : null,
    interest_rate: parseFloat(offer.interest_rate),
    comparison_rate: parseFloat(offer.comparison_rate),
    cash_back_value: parseFloat(offer.cash_back),
    discretionary_rate: offer.discretionary_rate,
    ongoing_fees: calculateOngoingFees(offer.ongoing_fees_value, offer.ongoing_fees_period),
    application_fees: parseFloat(applicationFeesValue),
    offset_account: offer.offset,
    redraw_facility: offer.redraw,
    extra_repayments: offer.extra_repayments,
    split_loan_facility: offer.split_loan_facility,
    repayment_type: fetchedRepaymentType && fetchedRepaymentType.value ?
      fetchedRepaymentType.value : undefined,
  };
}

export function userTypePayload(user) {
  return {
    userType: user.user_type,
  };
}

export function errorPayload(user, error) {
  return {
    email: user.email,
    error,
  };
}

export function identifyPayload(user) {
  if (!user) {
    return {};
  }

  return {
    user_id: user.auth0Id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    phone: getPhoneNumber(user),
    address: {
      state: user.address && user.address.state ? user.address.state : '',
      postalCode: user.address && user.address.state ? user.address.postcode : '',
    },
    user_type: user.user_type,
  };
}

export function userPayload(user) {
  if (!user) {
    return {};
  }

  return {
    user_id: user.uuid,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    phone: getPhoneNumber(user),
    address: {
      state: user.address && user.address.state ? user.address.state : '',
      postalCode: user.address && user.address.state ? user.address.postcode : '',
    },
    user_type: user.user_type,
  };
}

export function providerPayload(enquiryUuid, user) {
  return {
    enquiryId: enquiryUuid,
    providerId: user.uuid,
    providerFirstName: user.first_name,
    providerLastName: user.last_name,
    providerRating: parseFloat(user.fsp.rating),
    providerExperience: `${getProviderExperience(user.fsp)} years`,
  };
}

export function messagePayload(enquiry, provider, message = null) {
  return {
    enquiryId: enquiry.uuid,
    providerId: provider.uuid,
    providerFirstName: provider.first_name,
    providerLastName: provider.last_name,
    messageId: isNil(message) ? null : message.uuid,
  };
}

export function rankingPayload(user, enquiry, rankingPreference, stage) {
  return {
    user_id: user.uuid,
    enquiry_id: enquiry.uuid,
    ranking_preference: rankingPreference,
    enquiry_stage: stage,
  };
}

/* eslint-disable camelcase */
function getPhoneNumber({ country_code, mobile_number }) {
  if (!country_code || !mobile_number) {
    return '';
  }

  country_code = country_code.toString();
  mobile_number = mobile_number.toString();

  if (country_code.indexOf('61') >= 0 && mobile_number.charAt(0) === '0') {
    mobile_number = mobile_number.substr(1);
  }

  if (!country_code.startsWith('+') && country_code.indexOf('61') >= 0) {
    country_code = `+${country_code}`;
  }

  return country_code + mobile_number;
}

function calculateOngoingFees(value, period) {
  if (!value && !period) {
    return undefined;
  }

  if (!period) {
    return parseFloat(value);
  }

  if (period === 'per month') {
    return parseFloat(value) * 12;
  }

  return parseFloat(value);
}

function toUpper(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.substr(1))
    .join(' ');
}
